import React from 'react';
import en from '../../../translations/en.json';

function BasicDetailsTab({ 
  artist, 
  isEditing, 
  editName, 
  setEditName, 
  editGender, 
  setEditGender, 
  editStatus, 
  setEditStatus, 
  editTypes, 
  setEditTypes 
}) {
  const t = (key) => {
    return key.split('.').reduce((obj, i) => (obj ? obj[i] : null), en) || key;
  };

  const capitalize = (str) => str.charAt(0).toUpperCase() + str.slice(1);

  const getGenderLabel = (g) => {
    if (g === 'M' || g === 'Male') return 'Male';
    if (g === 'F' || g === 'Female') return 'Female';
    if (g === 'O' || g === 'Other/Band' || g === 'Other/Group') return 'Other/Band';
    return g || 'Male';
  };

  // Safe defaults
  const name = artist.name || 'Artist Name';
  const code = artist.code || artist.artist_code || 'ART-000123';
  const types = artist.types || ['singer', 'music', 'lyrics'];
  const gender = getGenderLabel(artist.gender);

  return (
    <div className="artist-basic-details-tab">
      {/* Tab Section Header Area */}
      <div className="tab-header-box">
        <div className="tab-icon-circle">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#0b66e3" strokeWidth="2.5">
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
            <circle cx="12" cy="7" r="4" />
          </svg>
        </div>
        <div className="tab-title-group">
          <h3 className="tab-title-text">{t('artistDetails.basicDetailsTitle')}</h3>
          <p className="tab-subtitle-text">{t('artistDetails.basicDetailsSubtitle')}</p>
        </div>
      </div>

      {/* Details List block */}
      <div className="artist-details-info-table">
        {/* Row 1: Name */}
        <div className="details-info-row">
          <div className="info-label-cell">
            <div className="info-icon-box">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                <circle cx="12" cy="7" r="4" />
              </svg>
            </div>
            <span className="field-name">{t('artistDetails.labelArtistName')}</span>
          </div>
          <span className="info-separator">:</span>
          <div className="info-value-cell bold">
            {isEditing ? (
              <input
                type="text"
                className="form-text-input"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                style={{ padding: '8px', border: '1px solid #cbd5e1', borderRadius: '6px', width: '100%', maxWidth: '400px', outline: 'none' }}
              />
            ) : (
              name
            )}
          </div>
        </div>

        {/* Row 2: Code */}
        <div className="details-info-row">
          <div className="info-label-cell">
            <div className="info-icon-box">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
                <line x1="7" y1="7" x2="7.01" y2="7" />
              </svg>
            </div>
            <span className="field-name">{t('artistDetails.labelArtistCode')}</span>
          </div>
          <span className="info-separator">:</span>
          <div className="info-value-cell code-font">{code}</div>
        </div>

        {/* Row 3: Tags */}
        <div className="details-info-row">
          <div className="info-label-cell">
            <div className="info-icon-box">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <line x1="4" y1="9" x2="20" y2="9" />
                <line x1="4" y1="15" x2="20" y2="15" />
                <line x1="10" y1="3" x2="8" y2="21" />
                <line x1="16" y1="3" x2="14" y2="21" />
              </svg>
            </div>
            <span className="field-name">{t('artistDetails.labelTags')}</span>
          </div>
          <span className="info-separator">:</span>
          <div className="info-value-cell tags-list">
            {isEditing ? (
              <div className="artist-type-toggles-container" style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                {['Music', 'Lyrics', 'Singer', 'Band', 'Other'].map(type => {
                  const key = type.toLowerCase();
                  const isChecked = editTypes[key];
                  return (
                    <label key={type} className={`artist-type-toggle-box ${isChecked ? 'active' : ''}`} style={{ fontSize: '13px', padding: '6px 12px', cursor: 'pointer' }}>
                      <span className="type-switch">
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => setEditTypes(prev => ({ ...prev, [key]: !prev[key] }))}
                        />
                        <span className="type-slider"></span>
                      </span>
                      <span className="type-label">{type}</span>
                    </label>
                  );
                })}
              </div>
            ) : (
              types.map((type) => (
                <span key={type} className={`tag-badge-item tag-${type.toLowerCase()}`}>
                  {capitalize(type)}
                </span>
              ))
            )}
          </div>
        </div>

        {/* Row 4: Gender */}
        <div className="details-info-row">
          <div className="info-label-cell">
            <div className="info-icon-box">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <circle cx="10" cy="14" r="5" />
                <line x1="10" y1="9" x2="10" y2="2" />
                <line x1="7" y1="5" x2="13" y2="5" />
                <line x1="14" y1="10" x2="19" y2="5" />
                <line x1="19" y1="5" x2="15" y2="5" />
                <line x1="19" y1="5" x2="19" y2="9" />
              </svg>
            </div>
            <span className="field-name">{t('artistDetails.labelGender')}</span>
          </div>
          <span className="info-separator">:</span>
          <div className="info-value-cell">
            {isEditing ? (
              <select
                value={editGender}
                onChange={(e) => setEditGender(e.target.value)}
                className="form-text-input"
                style={{ padding: '8px', border: '1px solid #cbd5e1', borderRadius: '6px', width: '100%', maxWidth: '400px', background: '#fff', outline: 'none' }}
              >
                <option value="Male">Male</option>
                <option value="Female">Female</option>
                <option value="Other/Band">Other/Band</option>
              </select>
            ) : (
              capitalize(gender)
            )}
          </div>
        </div>

        {/* Row 5: Status */}
        <div className="details-info-row">
          <div className="info-label-cell">
            <div className="info-icon-box">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <circle cx="12" cy="12" r="10" />
                <path d="m9 12 2 2 4-4" />
              </svg>
            </div>
            <span className="field-name">Status</span>
          </div>
          <span className="info-separator">:</span>
          <div className="info-value-cell">
            <span className={`status-badge ${(artist.status === 'Active' || artist.status === true || artist.status === 'true') ? 'active' : 'inactive'}`} style={{
              padding: '4px 8px',
              borderRadius: '12px',
              fontSize: '12px',
              fontWeight: '600',
              backgroundColor: (artist.status === 'Active' || artist.status === true || artist.status === 'true') ? '#e6f4ea' : '#fce8e6',
              color: (artist.status === 'Active' || artist.status === true || artist.status === 'true') ? '#137333' : '#c5221f'
            }}>
              {(artist.status === 'Active' || artist.status === true || artist.status === 'true') ? 'Active' : 'Inactive'}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default BasicDetailsTab;
